// STATIC AD (Pinterest Optimized)
// Standalone, brand-level visual creative — independent of the 4 video pillars.
// Produces ONE universal niche-focused image concept, then optionally renders
// the actual image via gpt-image-1.

import React, { useMemo, useRef, useState } from "react";
import {
  Image as ImageIcon, Loader2, Sparkles, Download,
  Pin, Eye, Heart, Share2, RefreshCw, Copy as CopyIcon,
  Upload, Wand2, Camera,
} from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { chatJSON, generateStaticAdImage, analyzeImageWithVision } from "../lib/openai";
import { buildStaticAdPrompt, wrapStaticAdImagePrompt, buildVisionStaticAdPrompt } from "../lib/prompts";
import { composeStaticAdOverlay } from "../lib/staticAdCompositor";

function copyText(text, label = "Copied") {
  if (!text) return;
  try {
    navigator.clipboard.writeText(text);
    toast.success(label);
  } catch (_) { /* noop */ }
}

export const StaticAdPanel = ({ apiKey, niche = "", audience = "", offer = "", trendsDigest = "" }) => {
  // Image source mode: "ai" = generate via gpt-image-1, "upload" = analyze a
  // user-uploaded photo with GPT-4o Vision and composite onto it.
  const [mode, setMode] = useState("ai");
  const [conceptLoading, setConceptLoading] = useState(false);
  const [imageLoading, setImageLoading]     = useState(false);
  const [visionLoading, setVisionLoading]   = useState(false);
  const [concept, setConcept]               = useState(null); // raw JSON
  const [imageDataUrl, setImageDataUrl]     = useState(null);
  const [uploadedPhoto, setUploadedPhoto]   = useState(null); // {dataUrl, name}
  const [error, setError]                   = useState("");
  const photoInputRef = useRef(null);

  const canGenerateConcept = useMemo(() => !!apiKey && !conceptLoading, [apiKey, conceptLoading]);
  const canRenderImage     = !!concept?.image_prompt && !imageLoading && !!apiKey;
  const canAnalyzePhoto    = !!apiKey && !!uploadedPhoto && !visionLoading;

  const reset = () => {
    setConcept(null);
    setImageDataUrl(null);
    setError("");
  };

  const switchMode = (next) => {
    if (next === mode) return;
    setMode(next);
    reset();
    if (next === "ai") setUploadedPhoto(null);
  };

  const generateConcept = async () => {
    if (!apiKey) { toast.error("Connect your OpenAI key first"); return; }
    setConceptLoading(true);
    setError("");
    setImageDataUrl(null);
    try {
      const { system, user } = buildStaticAdPrompt({ niche, audience, offer, trendsDigest });
      const result = await chatJSON({ apiKey, system, user, temperature: 0.95 });
      // Light validation.
      if (!result?.image_prompt) throw new Error("No image prompt returned");
      setConcept(result);
      toast.success("Static ad concept ready");
    } catch (e) {
      setError(e.message || "Concept generation failed");
      toast.error(e.message || "Concept generation failed");
    } finally {
      setConceptLoading(false);
    }
  };

  const renderImage = async () => {
    if (!apiKey || !concept?.image_prompt) return;
    setImageLoading(true);
    try {
      const placement = concept?.overlay_placement === "top" ? "top" : "bottom";
      const fullPrompt = wrapStaticAdImagePrompt(concept.image_prompt, { niche, overlayPlacement: placement });
      const { dataUrl: rawDataUrl } = await generateStaticAdImage({ apiKey, fullPrompt, quality: "high" });
      // Composite the Pinterest hook headline onto the text-free image in the browser.
      const composited = await composeStaticAdOverlay({
        imageDataUrl: rawDataUrl,
        overlayText: concept.overlay_text || "",
        placement,
      });
      setImageDataUrl(composited);
      toast.success("Pinterest static ad ready");
    } catch (e) {
      toast.error(e.message || "Image render failed");
    } finally {
      setImageLoading(false);
    }
  };

  // Read a File into a base64 data URL.
  const fileToDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = () => rej(new Error("Failed to read photo"));
    r.readAsDataURL(file);
  });

  const handlePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image (JPG, PNG, WebP)");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setUploadedPhoto({ dataUrl, name: file.name, size: file.size });
      // Reset prior concept/composite when a fresh photo lands.
      setConcept(null);
      setImageDataUrl(null);
      toast.success("Photo loaded — analyse it next");
    } catch (err) {
      toast.error(err?.message || "Photo upload failed");
    }
  };

  const analyzeAndComposite = async () => {
    if (!apiKey || !uploadedPhoto) { toast.error("Upload a photo first"); return; }
    setVisionLoading(true);
    setError("");
    setImageDataUrl(null);
    try {
      const { system, user } = buildVisionStaticAdPrompt({ niche, audience, offer });
      const result = await analyzeImageWithVision({
        apiKey,
        imageDataUrl: uploadedPhoto.dataUrl,
        system,
        user,
      });
      if (!result?.overlay_text) throw new Error("Vision didn't return a headline");
      setConcept(result);

      // Auto-composite onto the uploaded photo using Vision's chosen placement
      // + colour treatment + optional supporting text.
      const composited = await composeStaticAdOverlay({
        imageDataUrl: uploadedPhoto.dataUrl,
        overlayText: result.overlay_text || "",
        supportingText: result.supporting_text || "",
        placement: result.overlay_placement || "bottom",
        colorTreatment: result.text_color_treatment === "dark" ? "dark" : "light",
      });
      setImageDataUrl(composited);
      toast.success("Headline composed onto your photo");
    } catch (e) {
      setError(e.message || "Vision analysis failed");
      toast.error(e.message || "Vision analysis failed");
    } finally {
      setVisionLoading(false);
    }
  };

  const downloadImage = () => {
    if (!imageDataUrl) return;
    const slug = (niche || "static-ad").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 50) || "static-ad";
    const a = document.createElement("a");
    a.href = imageDataUrl;
    a.download = `${slug}-pinterest-static-ad.png`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <section
      data-testid="static-ad-panel"
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-zinc-950 via-[#10081a] to-zinc-950 p-6 sm:p-9 mb-6"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full blur-3xl bg-gradient-to-br from-rose-500/20 via-fuchsia-500/15 to-amber-500/15" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-[360px] w-[360px] rounded-full blur-3xl bg-gradient-to-tr from-amber-400/15 to-rose-400/10" />

      {/* Header */}
      <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-7">
        <div className="space-y-3 max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.28em] text-rose-200 shadow-[0_0_28px_-6px_rgba(244,63,94,0.6)]">
            <Pin className="h-3 w-3" strokeWidth={2.5} />
            Brand-level · top-of-funnel creative
          </div>
          <h3 className="font-display text-3xl sm:text-[34px] font-black text-white leading-[1.05] tracking-tight">
            STATIC AD{" "}
            <span className="bg-gradient-to-r from-rose-300 via-fuchsia-300 to-amber-300 bg-clip-text text-transparent">
              (Pinterest Optimized)
            </span>
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed max-w-xl">
            One universal niche-focused image concept — built to stop the
            Pinterest scroll, signal identity, and earn saves. Not tied to any
            individual video pillar.
          </p>
        </div>
        <div className="hidden lg:flex flex-col gap-1.5 text-right text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">
          <span className="inline-flex items-center gap-1.5"><Eye   className="h-3 w-3 text-rose-300" strokeWidth={2.5} /> Scroll-stopping</span>
          <span className="inline-flex items-center gap-1.5"><Heart className="h-3 w-3 text-rose-300" strokeWidth={2.5} /> Emotionally resonant</span>
          <span className="inline-flex items-center gap-1.5"><Share2 className="h-3 w-3 text-rose-300" strokeWidth={2.5} /> Save / share-worthy</span>
        </div>
      </div>

      {/* Mode toggle: AI Generated vs Upload My Own Photo */}
      <div
        data-testid="static-ad-mode-toggle"
        className="relative mb-5 inline-flex items-center gap-1 rounded-full border border-white/10 bg-zinc-950/70 p-1"
      >
        <button
          type="button"
          onClick={() => switchMode("ai")}
          data-testid="static-ad-mode-ai"
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-semibold tracking-tight transition-all ${
            mode === "ai"
              ? "bg-gradient-to-r from-rose-500 via-fuchsia-500 to-amber-500 text-white shadow-[0_10px_28px_-10px_rgba(244,63,94,0.55)]"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <Wand2 className="h-3.5 w-3.5" strokeWidth={2.4} />
          AI Generated Image
        </button>
        <button
          type="button"
          onClick={() => switchMode("upload")}
          data-testid="static-ad-mode-upload"
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[12px] font-semibold tracking-tight transition-all ${
            mode === "upload"
              ? "bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500 text-white shadow-[0_10px_28px_-10px_rgba(16,185,129,0.55)]"
              : "text-zinc-400 hover:text-zinc-200"
          }`}
        >
          <Upload className="h-3.5 w-3.5" strokeWidth={2.4} />
          Upload My Own Photo
        </button>
      </div>

      {/* Generate concept (AI mode) */}
      {mode === "ai" && !concept && (
        <div className="relative rounded-2xl border border-white/10 bg-zinc-950/70 backdrop-blur-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-zinc-300 leading-relaxed max-w-md">
            Generate a single Pinterest-native static ad concept that
            represents the whole niche — niche identity, the core problem, the
            transformation, and the lifestyle aspiration in one frame.
          </p>
          <Button
            type="button"
            onClick={generateConcept}
            disabled={!canGenerateConcept}
            data-testid="static-ad-generate-concept-button"
            className="group relative h-12 px-6 rounded-full font-semibold text-sm text-white
                       bg-gradient-to-r from-rose-500 via-fuchsia-500 to-amber-500
                       hover:from-rose-400 hover:via-fuchsia-400 hover:to-amber-400
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-300
                       shadow-[0_12px_36px_-12px_rgba(244,63,94,0.55)]
                       hover:shadow-[0_18px_48px_-10px_rgba(244,63,94,0.75)]
                       hover:-translate-y-[1px]
                       inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {conceptLoading ? (
              <><Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} /> Designing concept…</>
            ) : (
              <><Sparkles className="h-4 w-4 transition-transform group-hover:rotate-12" strokeWidth={2.5} /> Generate Static Ad</>
            )}
          </Button>
        </div>
      )}

      {/* Upload-mode UI: pick a photo, then Vision-analyse + composite */}
      {mode === "upload" && (
        <div
          data-testid="static-ad-upload-mode"
          className="relative rounded-2xl border border-emerald-500/30 bg-zinc-950/70 backdrop-blur-sm p-5 sm:p-6 space-y-4"
        >
          <input
            ref={photoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handlePhotoFile}
            data-testid="static-ad-photo-input"
            className="hidden"
          />

          {!uploadedPhoto ? (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-zinc-300 leading-relaxed max-w-md">
                Drop your own Pinterest-style photo. <span className="text-emerald-300 font-semibold">GPT-4o Vision</span> reads its composition, mood, and negative space, then writes a headline that lands exactly where the photo wants it.
              </p>
              <Button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                data-testid="static-ad-pick-photo-button"
                className="h-12 px-6 rounded-full font-semibold text-sm text-white bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500 hover:brightness-110 shadow-[0_12px_36px_-12px_rgba(16,185,129,0.55)] inline-flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Upload className="h-4 w-4" strokeWidth={2.5} />
                Pick a photo
              </Button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <img
                  src={uploadedPhoto.dataUrl}
                  alt={uploadedPhoto.name}
                  data-testid="static-ad-uploaded-thumb"
                  className="h-16 w-16 rounded-xl object-cover border border-emerald-500/30"
                />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300">
                    <Camera className="h-3 w-3" strokeWidth={2.4} />
                    Your photo
                  </div>
                  <div className="text-[12.5px] text-zinc-200 truncate font-semibold mt-0.5">
                    {uploadedPhoto.name}
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {(uploadedPhoto.size / 1024).toFixed(0)} KB · ready for Vision
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="static-ad-replace-photo-button"
                  className="h-9 rounded-full border border-white/10 hover:border-emerald-400/50 hover:bg-emerald-500/10 hover:text-emerald-200 text-[11px] px-3"
                >
                  Replace
                </Button>
                <Button
                  type="button"
                  onClick={analyzeAndComposite}
                  disabled={!canAnalyzePhoto}
                  data-testid="static-ad-analyze-photo-button"
                  className="h-10 px-5 rounded-full font-semibold text-xs text-white bg-gradient-to-r from-emerald-500 via-cyan-500 to-violet-500 hover:brightness-110 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {visionLoading ? (
                    <><Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} /> Analysing photo…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} /> Analyse with Vision</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div data-testid="static-ad-error" className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-200">
          {error}
        </div>
      )}

      {/* Concept output + image */}
      {concept && (
        <div className="relative grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-5 mt-2">
          {/* LEFT — Concept card */}
          <div className="rounded-2xl border border-rose-500/30 bg-gradient-to-br from-rose-500/10 via-zinc-950/70 to-zinc-950/70 p-5 space-y-4 shadow-[0_0_28px_-12px_rgba(244,63,94,0.4)]">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-[0.28em] text-rose-300">
                  Concept
                </span>
                <h4 className="font-display text-xl font-bold text-white mt-1 leading-tight">
                  {concept.concept_title || "Static ad concept"}
                </h4>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={mode === "upload" ? analyzeAndComposite : generateConcept}
                disabled={conceptLoading || visionLoading}
                data-testid="static-ad-regenerate-concept-button"
                className="h-7 text-[11px] text-rose-200 hover:text-rose-100 px-2"
                title={mode === "upload" ? "Re-analyse photo" : "Try a different concept"}
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${(conceptLoading || visionLoading) ? "animate-spin" : ""}`} strokeWidth={2.5} />
                {mode === "upload" ? "Re-analyse" : "Regenerate"}
              </Button>
            </div>

            {concept.overlay_text && (
              <div
                data-testid="static-ad-overlay-text"
                className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 space-y-1.5 shadow-[0_0_24px_-10px_rgba(245,158,11,0.45)]"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">
                    Pinterest Overlay Headline
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-200/60">
                    {(concept.overlay_placement || "bottom")} · {mode === "upload" ? "Vision · composited in-browser" : "composited in-browser"}
                  </span>
                </div>
                <p className="font-display text-[18px] leading-tight text-amber-50 font-bold">
                  &ldquo;{concept.overlay_text}&rdquo;
                </p>
                {mode === "upload" && concept.supporting_text && (
                  <p data-testid="static-ad-supporting-text" className="text-[12.5px] leading-snug text-amber-100/80 italic mt-1">
                    + {concept.supporting_text}
                  </p>
                )}
              </div>
            )}

            {concept.visual_summary && (
              <div className="text-sm text-zinc-200 leading-relaxed italic">
                &ldquo;{concept.visual_summary}&rdquo;
              </div>
            )}

            {concept.image_prompt && (
              <Block label="Image prompt" copyText={concept.image_prompt}>
                <div className="font-mono text-[12px] text-zinc-100 leading-relaxed whitespace-pre-wrap">
                  {concept.image_prompt}
                </div>
              </Block>
            )}

            {mode === "upload" && concept.negative_space_observation && (
              <Block label="Negative space observation" copyText={concept.negative_space_observation}>
                <div className="text-[12.5px] text-zinc-100 leading-relaxed">
                  {concept.negative_space_observation}
                </div>
              </Block>
            )}

            {concept.why_this_works && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <ReasonCell label="Niche identification"  value={concept.why_this_works.niche_identification} />
                <ReasonCell label="Emotional resonance"   value={concept.why_this_works.emotional_resonance} />
                <ReasonCell label="Transformation signal" value={concept.why_this_works.transformation_signal} />
                <ReasonCell label="Save / share trigger"  value={concept.why_this_works.save_share_curiosity} />
              </div>
            )}

            {(concept.audience_identity_cues?.length || concept.mood_keywords?.length) ? (
              <div className="space-y-2 pt-1">
                {concept.audience_identity_cues?.length ? (
                  <TagRow label="Identity cues" items={concept.audience_identity_cues} accent="amber" />
                ) : null}
                {concept.mood_keywords?.length ? (
                  <TagRow label="Mood" items={concept.mood_keywords} accent="rose" />
                ) : null}
              </div>
            ) : null}

            <div className="pt-2 flex flex-col sm:flex-row gap-2">
              {mode === "ai" && (
                <Button
                  type="button"
                  onClick={renderImage}
                  disabled={!canRenderImage}
                  data-testid="static-ad-render-image-button"
                  className="h-10 rounded-full bg-gradient-to-r from-amber-500 to-rose-500 hover:brightness-110 text-[#1c1302] font-semibold text-xs px-4 shadow-[0_10px_28px_-10px_rgba(244,63,94,0.55)] disabled:opacity-50"
                >
                  {imageLoading ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" strokeWidth={2} /> Rendering image…</>
                  ) : imageDataUrl ? (
                    <><Sparkles className="h-3.5 w-3.5 mr-1.5" strokeWidth={2.5} /> Re-render image</>
                  ) : (
                    <><ImageIcon className="h-3.5 w-3.5 mr-1.5" strokeWidth={2.5} /> Render image</>
                  )}
                </Button>
              )}
              {imageDataUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={downloadImage}
                  data-testid="static-ad-download-button"
                  className="h-10 rounded-full border border-white/10 text-zinc-200 hover:text-white hover:bg-zinc-800 px-4 text-xs"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={2} />
                  Download PNG
                </Button>
              )}
            </div>
          </div>

          {/* RIGHT — Image preview */}
          <div className="rounded-2xl border border-white/10 bg-zinc-950/70 p-5 flex flex-col items-center justify-center min-h-[320px]">
            {imageDataUrl ? (
              <div className="w-full max-w-[320px] mx-auto">
                <img
                  src={imageDataUrl}
                  alt="Static ad creative"
                  data-testid="static-ad-image"
                  className="w-full rounded-2xl border border-white/10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
                />
                <p className="text-[11px] text-center text-zinc-500 mt-3 italic">
                  Pinterest-optimised · 2:3 vertical · overlay composited in-browser
                </p>
              </div>
            ) : (
              <div className="text-center text-zinc-500 max-w-[260px] space-y-3">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-300">
                  <ImageIcon className="h-6 w-6" strokeWidth={1.6} />
                </div>
                <p className="text-[12.5px] leading-relaxed">
                  Render the image when you&apos;re happy with the concept. Iterate
                  the concept first to keep credits efficient.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

// ─────── presentational helpers ───────
const Block = ({ label, copyText: ct, children }) => (
  <div className="rounded-xl border border-white/10 bg-zinc-950/80 p-3.5 space-y-2">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500">{label}</span>
      {ct && (
        <button
          type="button"
          onClick={() => copyText(ct, `${label} copied`)}
          className="inline-flex items-center gap-1 h-6 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300 hover:text-white text-[10px] font-semibold tracking-wide px-2 transition-all"
        >
          <CopyIcon className="h-3 w-3" strokeWidth={2} />
          Copy
        </button>
      )}
    </div>
    {children}
  </div>
);

const ReasonCell = ({ label, value }) => (
  <div className="rounded-lg border border-white/10 bg-zinc-950/80 p-2.5">
    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-200/70">{label}</div>
    <div className="text-[12.5px] text-zinc-100 leading-snug mt-1">{value || "—"}</div>
  </div>
);

const TagRow = ({ label, items, accent = "rose" }) => {
  const accentClasses = accent === "amber"
    ? "border-amber-500/40 bg-amber-500/10 text-amber-100"
    : "border-rose-500/40 bg-rose-500/10 text-rose-100";
  return (
    <div className="flex items-center flex-wrap gap-1.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-500 mr-1">
        {label}
      </span>
      {items.map((t, i) => (
        <span
          key={`${label}-${i}`}
          className={`text-[10.5px] font-semibold tracking-wide rounded-full border px-2 py-0.5 ${accentClasses}`}
        >
          {t}
        </span>
      ))}
    </div>
  );
};

export default StaticAdPanel;
